import { Link } from "react-router-dom";

const SECTIONS = [
  {
    title: "1. Acceptance",
    body: (
      <p>
        By creating an account or using Zenith360 HR (“the Service”), you agree to these Terms of Use.
        The Service is owned and operated by <b>ADB Consulting LLP</b> (“Zenith360”, “we”, “our”).
        If you do not agree, please do not use the Service.
      </p>
    ),
  },
  {
    title: "2. Accounts and access",
    body: (
      <>
        <p>You must be at least 18 to create an account. You are responsible for keeping your credentials safe. All activity performed with your credentials is treated as yours.</p>
        <p>Zenith360 supports three account types — <b>Organisation</b>, <b>Group of organisations</b>, and <b>Agency</b>. The account owner sets the subscription tier and adds administrators; administrators may add end-users (employees, candidates, clients) subject to the applicable seat limits.</p>
      </>
    ),
  },
  {
    title: "3. Subscription, billing and refunds",
    body: (
      <>
        <p>Paid plans are billed monthly or annually via Razorpay. All prices are shown in the currency indicated at checkout and are exclusive of applicable taxes (e.g. GST).</p>
        <p>Cancellations take effect at the end of the current billing cycle — you keep access to the paid features until then. We do not offer pro-rata refunds for partial periods, except where required by law. A free 14-day trial is available for new tenants and can be cancelled anytime with no charge.</p>
      </>
    ),
  },
  {
    title: "4. Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Upload content that is unlawful, defamatory, obscene, or infringes third-party rights.</li>
          <li>Reverse-engineer, scrape, or attempt to bypass the Service’s access controls.</li>
          <li>Use the Service to send unsolicited communication (spam), track users without their consent, or violate any applicable labour or data-protection law.</li>
          <li>Sub-license, resell, or white-label the Service without a written agreement with us.</li>
        </ul>
      </>
    ),
  },
  {
    title: "5. Your content",
    body: (
      <p>
        You retain ownership of everything you upload — employee records, documents, letters, custom policies. You
        grant Zenith360 a limited, worldwide licence to host, process, and display that content solely to provide
        the Service. If you close your account, see our{" "}
        <Link to="/delete-account" className="text-adb-navy underline">account-deletion page</Link> for how we
        handle your data thereafter.
      </p>
    ),
  },
  {
    title: "6. Availability and support",
    body: (
      <>
        <p>We target 99.5% monthly uptime for paid plans. Scheduled maintenance is announced at least 24 h in advance. Support is offered via email (<a href="mailto:support@zenith360-hr.com" className="text-adb-navy underline">support@zenith360-hr.com</a>) on business days (India Standard Time). Premium SLAs are available under a separate agreement.</p>
      </>
    ),
  },
  {
    title: "7. Third-party services",
    body: (
      <p>
        Zenith360 relies on carefully chosen sub-processors — MongoDB Atlas, Razorpay, Firebase Cloud Messaging,
        Emergent Object Storage, Anthropic / OpenAI / Google for AI features, and OpenStreetMap Nominatim. Their
        terms and privacy policies apply to the specific interactions they process. See our{" "}
        <Link to="/privacy" className="text-adb-navy underline">Privacy Policy</Link> for the full list.
      </p>
    ),
  },
  {
    title: "8. AI-generated content",
    body: (
      <p>
        Certain features (Ask Zenith360, AI Studio for JD / KRA / KPI generation, help-center copy) use large
        language models. AI outputs are generated on-the-fly and may occasionally be inaccurate — always review
        AI-generated payroll, legal, or compliance suggestions before acting on them. You are responsible for the
        final decisions taken based on AI output.
      </p>
    ),
  },
  {
    title: "9. Warranty disclaimer",
    body: (
      <p>
        The Service is provided <b>as is</b>. To the maximum extent permitted by law we disclaim all implied
        warranties of merchantability, fitness for a particular purpose, and non-infringement. Zenith360 does not
        guarantee that the Service will be uninterrupted or error-free.
      </p>
    ),
  },
  {
    title: "10. Limitation of liability",
    body: (
      <p>
        In no event shall Zenith360 or its officers be liable for indirect, incidental, or consequential damages,
        including lost profits or lost data. Our total liability for any claim shall not exceed the amount you paid
        us in the 12 months preceding the claim.
      </p>
    ),
  },
  {
    title: "11. Termination",
    body: (
      <p>
        You may cancel at any time from your billing settings. We may suspend or terminate your account for a
        material breach of these Terms after 14 days’ written notice, or immediately for illegal activity, fraud,
        or serious security incidents.
      </p>
    ),
  },
  {
    title: "12. Governing law and disputes",
    body: (
      <p>
        These Terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction
        of the courts of Mumbai, Maharashtra.
      </p>
    ),
  },
  {
    title: "13. Contact",
    body: (
      <p>Questions? Write to <a href="mailto:legal@zenith360-hr.com" className="text-adb-navy underline">legal@zenith360-hr.com</a> or <a href="mailto:hello@zenith360-hr.com" className="text-adb-navy underline">hello@zenith360-hr.com</a>.</p>
    ),
  },
];

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="terms-page">
      <Link to="/" className="text-xs text-zenith-teal font-bold uppercase tracking-widest">← Back to Zenith360 HR</Link>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-adb-navy mt-4">Terms of Use</h1>
      <div className="text-xs text-zinc-500 mt-2">Last updated: 28 February 2026 · Version 1.0</div>

      <div className="prose prose-zinc mt-8 space-y-6 text-[15px] leading-relaxed text-zinc-700">
        {SECTIONS.map((s, i) => (
          <section key={i}>
            <h2 className="font-display text-xl font-bold text-adb-navy mt-6 mb-2">{s.title}</h2>
            {s.body}
          </section>
        ))}
        <hr className="border-zinc-200 my-8" />
        <p className="text-xs text-zinc-500">
          Zenith360 HR is a product of ADB Consulting LLP · Registered in India ·{" "}
          <a href="mailto:hello@zenith360-hr.com" className="text-adb-navy underline">hello@zenith360-hr.com</a>
        </p>
      </div>
    </div>
  );
}
